/**
 * PayslipGate — the ISSUE action on the pay-run screen, as a preview-then-commit
 * gate. Spec: docs/payslip-concept.md §A (eligibility), §C (permanence),
 * §D (restatement). Backend: PR 197.
 *
 * ── WHY THE CONFIRMATION IS HEAVIER THAN THE RECORD GATE'S ───────────────────
 * The record gate previews then commits behind a plain button. The correction
 * gate makes you type "supersede", because a correction rewrites what the
 * ledger says. Issuing a payslip is heavier than either: it produces a
 * STATUTORY DOCUMENT, stores its exact bytes forever, and — once slice 3 wires
 * sending — is the thing a worker receives. There is no edit and no delete;
 * 0084 grants the application SELECT and INSERT only, so nothing in this
 * platform can alter or remove what this button creates.
 *
 * So it carries the correction gate's typed-word pattern, with the word
 * "issue". The mechanism is deliberately the same as the one operators already
 * know rather than a novel one — a second confirmation dialog would be easy to
 * click through, and a checkbox has the same problem the s.8(2)(e) radio pair
 * exists to avoid. Typing a word cannot be done by accident.
 *
 * ── THE IDEMPOTENCY KEY ──────────────────────────────────────────────────────
 * ONE key is minted per SUCCESSFUL preview and reused on every retry of that
 * same previewed intent, exactly as the record and correction gates do. A
 * commit failure KEEPS the key: the retry must answer with the first write's
 * document if that write in fact landed, which is the entire reason the key
 * exists. Changing the target invalidates BOTH the preview and the key,
 * because a key that outlived its intent could bind a new target to an old
 * write.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Eye, FileText, X } from 'lucide-react'
import {
  previewPayslip, issuePayslip, fetchPayslipDocument,
} from '../../api'
import type { PayrollLedgerRow, PayslipPreview, PayslipDocument } from '../../types'
import { apiErrorMessage } from '../../../../api/client'

/** The word that arms the commit. Compared case-insensitively and trimmed: the
 *  point is deliberate reading, not typing accuracy. */
const CONFIRM_WORD = 'issue'

const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'

/** base64 → a blob URL for the preview's document. The preview's bytes come
 *  back inside JSON (base64) because that arm is JSON; the STORED document is
 *  fetched as bytes from its own route after the commit. */
function pdfObjectUrl(base64: string): string {
  const bin = atob(base64)
  const buf = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i)
  return URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }))
}

export default function PayslipGate({ row, onIssued, onClose }: {
  row: PayrollLedgerRow
  onIssued: () => void
  onClose: () => void
}) {
  const [previewed, setPreviewed] = useState<PayslipPreview | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The STORED document, fetched back after a successful commit. Showing this
  // rather than the commit response's bytes proves the ROUND TRIP: what the
  // operator ends up looking at came out of the database.
  const [stored, setStored] = useState<PayslipDocument | null>(null)

  // Changing the target invalidates the previewed intent AND its key. A key
  // that outlived its preview could bind a new target to an old write.
  //
  // Done as a RENDER-TIME adjustment (React's documented recipe for resetting
  // state when a prop changes) rather than in an effect: an effect would run
  // AFTER a paint in which the old preview was still on screen beside the new
  // target's name, and it is the pattern the repo's set-state-in-effect
  // warnings are about. React re-renders immediately without committing the
  // intermediate output.
  const [prevRowID, setPrevRowID] = useState(row.id)
  if (prevRowID !== row.id) {
    setPrevRowID(row.id)
    setPreviewed(null)
    setIdempotencyKey(null)
    setConfirmText('')
    setError(null)
    setStored(null)
    // The rendered DOCUMENT goes too. Leaving it up would show the previous
    // target's payslip beside the new target's name — the most confusing
    // possible state for a gate whose whole job is "look at this before you
    // commit to it". The [previewUrl] cleanup below revokes the old blob.
    setPreviewUrl(null)
  }

  // Object URLs are owned by this component and revoked when they are replaced
  // or the gate closes — a leaked blob URL pins the whole PDF in memory.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])
  useEffect(() => () => { if (stored) URL.revokeObjectURL(stored.blobUrl) }, [stored])

  const runPreview = useCallback(async () => {
    setBusy('preview')
    setError(null)
    try {
      const p = await previewPayslip(row.id)
      setPreviewed(p)
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return pdfObjectUrl(p.document) })
      // ONE key per successful preview.
      setIdempotencyKey(crypto.randomUUID())
      setConfirmText('')
    } catch (e) {
      // A refusal the commit would hit surfaces HERE, by name: the 422 for a
      // superseded row or absent s.8(2)(e) hours, the 409 for a version that
      // already carries a document. Rendered verbatim — the backend's message
      // names the field or fact at fault and the UI must not paraphrase it.
      setPreviewed(null)
      setIdempotencyKey(null)
      setError(apiErrorMessage(e, 'Previewing this payslip failed'))
    } finally {
      setBusy(null)
    }
  }, [row.id])

  const commit = useCallback(async () => {
    if (!idempotencyKey) return
    setBusy('commit')
    setError(null)
    try {
      const { preview } = await issuePayslip(row.id, idempotencyKey)
      // ROUND TRIP, not trust: the document now shown is fetched back from the
      // read route, so what the operator sees is the STORED artefact rather
      // than the bytes the commit happened to echo.
      const doc = await fetchPayslipDocument(preview.payslip.id)
      setStored(prev => { if (prev) URL.revokeObjectURL(prev.blobUrl); return doc })
      onIssued()
    } catch (e) {
      // The key is KEPT on failure — the retry must answer with the first
      // write's document if that write in fact landed.
      setError(apiErrorMessage(e, 'Issuing this payslip failed'))
    } finally {
      setBusy(null)
    }
  }, [row.id, idempotencyKey, onIssued])

  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD
  const canCommit = previewed !== null && idempotencyKey !== null && confirmed && busy === null

  const heading = useMemo(
    () => `${row.fullName} · ${row.taxYear} period ${row.periodNumber}`,
    [row.fullName, row.taxYear, row.periodNumber],
  )

  return (
    <div className="flex flex-col gap-4" data-testid="payslip-gate">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-14 font-semibold text-ink-primary">Issue payslip</div>
          <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
            {heading}. Preview first: the document below is rendered by the payroll
            engine and is exactly what will be stored. A payslip cannot be edited or
            deleted once issued.
          </div>
        </div>
        <button onClick={onClose} aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors flex-shrink-0">
          <X size={16} />
        </button>
      </div>

      {error && (
        <div role="alert" data-testid="payslip-refusal"
          className="text-12 leading-relaxed rounded-tile border border-edge-control bg-surface-control px-3 py-2 text-ink-body">
          {error}
        </div>
      )}

      {stored ? (
        <div className="flex flex-col gap-2" data-testid="payslip-issued">
          <div className="text-12 font-medium text-ink-primary">
            Issued. The document below is the stored file, fetched back from the record.
          </div>
          <div className="text-11 text-ink-muted tabular-nums break-all">
            sha256 {stored.digestSha256}
          </div>
          <iframe title="Issued payslip" src={stored.blobUrl}
            className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
        </div>
      ) : (
        <>
          {previewUrl && (
            <div className="flex flex-col gap-2">
              <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
                Preview — not yet issued
              </div>
              <iframe title="Payslip preview" src={previewUrl} data-testid="payslip-preview-frame"
                className="w-full h-[28rem] border border-edge-control rounded-tile bg-surface-control" />
              {previewed?.reissue && (
                <div className="text-12 text-ink-body leading-relaxed" data-testid="payslip-reissue-note">
                  This replaces an earlier statement for the same period. The document
                  carries that notice on its face.
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <button onClick={runPreview} disabled={busy !== null}
              className="k-btn-secondary k-btn-secondary--flex disabled:opacity-50">
              <Eye size={14} /> {busy === 'preview' ? 'Previewing…' : previewed ? 'Preview again' : 'Preview'}
            </button>

            {previewed && (
              <div>
                <label htmlFor="payslip-confirm" className={labelCls}>
                  Type “{CONFIRM_WORD}” to confirm
                </label>
                <input id="payslip-confirm" value={confirmText} autoComplete="off"
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  className="h-9 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent" />
              </div>
            )}

            <button onClick={commit} disabled={!canCommit}
              className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40">
              <Check size={14} /> {busy === 'commit' ? 'Issuing…' : 'Issue payslip'}
            </button>

            {!previewed && (
              <span className="text-12 text-ink-muted">Preview before issuing.</span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** PayslipCell — the pay-run row's payslip column.
 *
 *  Three states, and the third is the one that matters: a SUPERSEDED row never
 *  offers an issue action. Its document (if it has one) stays retrievable
 *  forever — superseding is not deleting — but a new statement can only come
 *  from the current version. The backend refuses it with a named 422 anyway;
 *  withholding the action here is defence in depth, not the enforcement. */
export function PayslipCell({ row, payslip, onIssue, onView }: {
  row: PayrollLedgerRow
  payslip: import('../../types').Payslip | undefined
  /** Absent for a role without records access (lib/writeAccess): an
   *  unissued current row then reads "Not issued" with no action. */
  onIssue?: (row: PayrollLedgerRow) => void
  onView: (p: import('../../types').Payslip) => void
}) {
  if (payslip) {
    return (
      <button onClick={() => onView(payslip)}
        data-testid={`payslip-view-${row.id}`}
        aria-label={`View payslip issued ${new Date(payslip.issuedAt).toLocaleDateString('en-GB')} for period ${row.periodNumber}`}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover whitespace-nowrap">
        <FileText size={12} /> {new Date(payslip.issuedAt).toLocaleDateString('en-GB')}
      </button>
    )
  }
  if (row.status === 'superseded') {
    // No action, and no invitation to one: this version can never produce a new
    // statement.
    return <span className="text-11 text-ink-muted">—</span>
  }
  if (!onIssue) return <span className="text-11 text-ink-muted">Not issued</span>
  return (
    <button onClick={() => onIssue(row)}
      data-testid={`payslip-issue-${row.id}`}
      aria-label={`Issue payslip for period ${row.periodNumber} for ${row.fullName}`}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover whitespace-nowrap">
      <FileText size={12} /> Issue payslip
    </button>
  )
}
